import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, TextField, Button, MenuItem, CircularProgress } from '@mui/material';
import { api, type Tenant, type CVModelProvider, type SubstrateModelProvider } from '../services/api';
import { useStore } from '../store/useStore';

const CV_MODEL_OPTIONS: { value: CVModelProvider; label: string; description: string }[] = [
  {
    value: 'local_yolo26',
    label: 'Local hosted (YOLO26)',
    description: 'Runs the fine-tuned YOLO26 instance-segmentation model in-process on the API server.',
  },
  {
    value: 'cloud_yolo26',
    label: 'Cloud hosted (YOLO26)',
    description: 'Runs the same YOLO26 model on a dedicated cloud inference endpoint. Not yet available.',
  },
  {
    value: 'roboflow_nas',
    label: 'Roboflow hosted (NAS)',
    description: 'Runs the SurfaceIQ MVP Roboflow Workflow (RF-DETR NAS instance segmentation) via the Roboflow serverless API.',
  },
];

const SUBSTRATE_MODEL_OPTIONS: { value: SubstrateModelProvider; label: string; description: string }[] = [
  {
    value: 'heuristic',
    label: 'Heuristic (default)',
    description: 'Deterministic placeholder pick — no real material recognition. No API key required.',
  },
  {
    value: 'anthropic_claude',
    label: 'Anthropic Claude',
    description: 'Uses Claude vision to identify the surface substrate (metal, wood, laminate, etc.) from the photo. Requires an Anthropic API key configured on the server.',
  },
  {
    value: 'openai_gpt4o',
    label: 'OpenAI GPT-4o',
    description: 'Uses OpenAI GPT-4o vision to identify the surface substrate. Requires an OpenAI API key configured on the server.',
  },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useStore();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [provider, setProvider] = useState<CVModelProvider>('local_yolo26');
  const [substrateProvider, setSubstrateProvider] = useState<SubstrateModelProvider>('heuristic');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const load = async () => {
      try {
        const tenantData = await api.getMyTenant();
        setTenant(tenantData);
        setProvider(tenantData.cv_model_provider);
        setSubstrateProvider(tenantData.substrate_model_provider);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    if (!tenant) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      const updated = await api.updateTenantSettings(tenant.id, provider, substrateProvider);
      setTenant(updated);
      setProvider(updated.cv_model_provider);
      setSubstrateProvider(updated.substrate_model_provider);
      setSuccess('Settings updated.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to update settings');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  if (!user) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ color: '#F1F5F9', mb: 2 }}>Settings</Typography>
      {loading ? (
        <CircularProgress sx={{ color: '#6366F1' }} />
      ) : !tenant ? (
        <Card sx={{ bgcolor: '#1E293B', p: 3 }}>
          <Typography sx={{ color: '#F1F5F9' }}>No tenant organization found for your account.</Typography>
        </Card>
      ) : (
        <Card sx={{ bgcolor: '#1E293B', border: '1px solid #334155', maxWidth: 640 }}>
          <CardContent>
            <Typography sx={{ color: '#F1F5F9', fontWeight: 700, mb: 1 }}>Defect Detection Model</Typography>
            <Typography sx={{ color: '#94A3B8', fontSize: 13, mb: 2 }}>
              Choose which computer vision backend analyzes surface photos for defects across your organization.
            </Typography>
            {error && <Typography sx={{ color: '#F87171', mb: 2 }}>{error}</Typography>}
            {success && <Typography sx={{ color: '#34D399', mb: 2 }}>{success}</Typography>}

            <TextField
              select
              fullWidth
              label="Computer Vision Model"
              value={provider}
              onChange={e => setProvider(e.target.value as CVModelProvider)}
              disabled={!isAdmin}
              helperText={!isAdmin ? 'Only tenant administrators can change this setting.' : undefined}
              InputLabelProps={{ sx: { color: '#64748B' } }}
              sx={{ mb: 1, '& .MuiOutlinedInput-root': { color: '#F1F5F9' } }}
            >
              {CV_MODEL_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <Typography sx={{ color: '#64748B', fontSize: 12, mb: 3 }}>
              {CV_MODEL_OPTIONS.find(o => o.value === provider)?.description}
            </Typography>

            <Typography sx={{ color: '#F1F5F9', fontWeight: 700, mb: 1 }}>Substrate Material Classifier</Typography>
            <Typography sx={{ color: '#94A3B8', fontSize: 13, mb: 2 }}>
              Choose which backend identifies surface substrate/material (metal, wood, laminate, etc.) for repaint scans and inspection photos.
            </Typography>

            <TextField
              select
              fullWidth
              label="Substrate Material Classifier"
              value={substrateProvider}
              onChange={e => setSubstrateProvider(e.target.value as SubstrateModelProvider)}
              disabled={!isAdmin}
              helperText={!isAdmin ? 'Only tenant administrators can change this setting.' : undefined}
              InputLabelProps={{ sx: { color: '#64748B' } }}
              sx={{ mb: 1, '& .MuiOutlinedInput-root': { color: '#F1F5F9' } }}
            >
              {SUBSTRATE_MODEL_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <Typography sx={{ color: '#64748B', fontSize: 12, mb: 3 }}>
              {SUBSTRATE_MODEL_OPTIONS.find(o => o.value === substrateProvider)?.description}
            </Typography>

            {isAdmin && (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || (provider === tenant.cv_model_provider && substrateProvider === tenant.substrate_model_provider)}
                sx={{ bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' } }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
